import _ from "lodash";

import type { FilesTableParams } from "@dashboard/files/components/UploadTable/UploadTable";
import { SourceType } from "@dashboard/files/types";
import { TimeRangeOptions } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import type { GroupFileTabParams } from "@dashboard/shared/components/SidePanel/GroupPanel/FileTab/FileTab";
import type { LicenseFileTabParams } from "@dashboard/shared/components/SidePanel/LicensePanel/FileTab/FileTab";
import { ColumnFiltersState, ExpandedState, SortingState } from "@tanstack/react-table";

import { LocalStorage } from "./localStorageService";

type IsRecord<T> = T extends object ? (T extends unknown[] ? false : true) : false;

type KeyPaths<T, Prev extends string = ""> = {
    [K in keyof T]: IsRecord<T[K]> extends true
        ? `${Prev}${K & string}` | KeyPaths<T[K], `${Prev}${K & string}.`>
        : `${Prev}${K & string}`;
}[keyof T];

export type CommonSettings = {
    drawerOpen: boolean;
    includeTerminatedWorkspaces: boolean;
    betaFeatures: Record<string, boolean>;
    registrationAttempts: Record<string, { email: string; timestamp: number }>;
    themeMode: "light" | "dark" | "browser";
};
type CommonSettingsPaths = KeyPaths<CommonSettings>;

type WorkspaceSettings = {
    [workspaceId: string]: {
        licenseSidePanel: {
            appsTab: {
                columnFilters: ColumnFiltersState;
            };
            fileTab: {
                params: LicenseFileTabParams;
            };
            hardwareTab: {
                expandedSections: Record<string, boolean>;
            };
        };
        groupSidePanel: {
            fileTab: {
                params: GroupFileTabParams;
            };
        };
        groupPage: {
            columnFilters: ColumnFiltersState;
            expanded: ExpandedState;
            search: string;
            rowsPerPage: number;
        };
        devicePage: {
            columnFilters: ColumnFiltersState;
            rowsPerPage: number;
            visibleColumns: Record<string, boolean>;
            sorting: SortingState;
            columnOrder: string[];
        };
        filePage: {
            params: FilesTableParams;
            treeOpen: boolean;
        };
        provisioningPage: {
            collapsedSections: Record<string, boolean>;
            lastSelectedProvisioning: number;
        };
        rolloutsPage: {
            lastSelectedHardware: string;
        };
        overviewPage: {
            groupFilter: number[];
            dayRange: number;
        };
        productPage: {
            lastSelectedProduct: number;
            collapsedSections: Record<string, boolean>;
        };
        workspacePage: {
            lastExpirationBannerDate: number;
        };
        downloadsPage: {
            rowsPerPage: number;
            sorting: SortingState;
            columnFilters: ColumnFiltersState;
        };
        clientsPage: {
            rowsPerPage: number;
            columnFilters: ColumnFiltersState;
        };
    };
};
type SingleWorkspaceSettings = WorkspaceSettings[string];
export type SingleWorkspaceSettingsPaths = KeyPaths<SingleWorkspaceSettings>;

type UserSettings = Partial<CommonSettings> & WorkspaceSettings;

const defaultDownloadsPageColumnFilters: ColumnFiltersState = [{ id: "channel", value: ["Live"] }];

export const defaultDevicePageVisibleColumns: Record<string, boolean> = {
    "batteryHealth": false,
    "batteryLevel": false,
    "batteryPlugged": false,
    "batteryStatus": false,
    "batteryVoltage": false,
    "chargeId": false,
    "code": true,
    "codename": false,
    "comment": false,
    "connection": false,
    "description": false,
    "ethernetMac": false,
    "groupName": true,
    "hardwareModel": false,
    "hardwareManufacturer": false,
    "hardwareProduct": false,
    "imei": false,
    "ipv4": false,
    "ipv6": false,
    "lastActivityDate": true,
    "licenseStatus": false,
    "memoryAvailable": false,
    "memoryTotal": false,
    "moveLicenseCheckbox": false,
    "name": true,
    "onlineSince": false,
    "onlineStatus": true,
    "serial": false,
    "status": true,
    "systemTime": false,
    "systemTimezone": false,
    "systemUptime": false,
    "systemVersion": true,
    "warnings": true,
    "wifiMac": false,
    "wifiNetwork": false,
};

class UserSettingsService {
    private static getUserSettings(): UserSettings {
        const settings = LocalStorage.getItem<UserSettings | null>("userSettings");
        return settings || {};
    }
    private static setUserSettings(value: Partial<UserSettings>) {
        LocalStorage.setItem("userSettings", value);
    }

    // common
    static setCommonValue<T>(key: CommonSettingsPaths, value: T) {
        const settings = this.getUserSettings();
        _.set(settings, key, value);
        this.setUserSettings(settings);
    }
    static getDrawerOpen() {
        const settings = this.getUserSettings();
        return settings?.drawerOpen ?? true;
    }
    static getIncludeTerminatedWorkspaces() {
        const settings = this.getUserSettings();
        return settings?.includeTerminatedWorkspaces || false;
    }
    static getBetaFeatures() {
        const settings = this.getUserSettings();
        return settings?.betaFeatures || {};
    }
    static getRegistrationAttempts() {
        const settings = this.getUserSettings();
        return settings?.registrationAttempts || {};
    }

    static addRegistrationAttempt(email: string) {
        const settings = this.getUserSettings();
        const attempts = settings?.registrationAttempts || {};
        const timestamp = Date.now();

        attempts[email] = { email, timestamp };
        this.setCommonValue("registrationAttempts", attempts);
    }

    static getThemeMode() {
        const settings = this.getUserSettings();
        return settings?.themeMode || "browser";
    }

    // workspace
    static setWorkspaceValue<T>(key: SingleWorkspaceSettingsPaths, value: T) {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");

        if (workspaceId) {
            const settings = this.getUserSettings();
            _.set(settings, `${workspaceId}.${key}`, value);
            this.setUserSettings(settings);
        }
    }

    static getDevicePageColumnFilters() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.devicePage?.columnFilters || [];
        }
        return [];
    }
    static getDevicePageRowsPerPage() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.devicePage?.rowsPerPage ?? 12;
        }
        return 12;
    }
    static getDevicePageVisibleColumns() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();
        if (workspaceId && settings?.[workspaceId]?.devicePage?.visibleColumns) {
            return settings?.[workspaceId]?.devicePage?.visibleColumns;
        }

        return defaultDevicePageVisibleColumns;
    }
    static getDevicePageSorting() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.devicePage?.sorting || [];
        }
        return [];
    }
    static getDevicePageColumnOrder(): string[] {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.devicePage?.columnOrder || [];
        }
        return [];
    }

    static getFilePageParams() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        const defaultParams: FilesTableParams = {
            workspaceId: 0,
            pageIndex: 0,
            pageSize: 12,
            sortCriteria: "modifiedDate",
            sortDirection: "desc",
            groups: [],
            uploaders: [],
            sourceTypes: [],
            includeParentFiles: false,
            filename: "",
        };

        if (workspaceId) {
            return { ...defaultParams, ...settings?.[workspaceId]?.filePage?.params };
        }
        return defaultParams;
    }

    static getFilePageTreeOpen(): boolean {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.filePage?.treeOpen ?? true;
        }
        return true;
    }

    static setFilePageTreeOpen(value: boolean) {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");

        if (workspaceId) {
            const settings = this.getUserSettings();
            _.set(settings, `${workspaceId}.filePage.treeOpen`, value);
            this.setUserSettings(settings);
        }
    }

    static getGroupSidePanelFileTabParams() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        const defaultParams: GroupFileTabParams = {
            workspaceId: 0,
            groups: [],
            sortCriteria: "filename",
            sortDirection: "desc",
            visibilities: [],
            includeParentFiles: false,
        };

        if (workspaceId) {
            const saved = settings?.[workspaceId]?.groupSidePanel?.fileTab?.params;
            if (!saved) {
                return { ...defaultParams };
            }
            return { ...defaultParams, ...saved };
        }
        return defaultParams;
    }
    static getGroupPageColumnFilters() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.groupPage?.columnFilters || [];
        }
        return [];
    }

    static getGroupPageExpanded() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.groupPage?.expanded || {};
        }
        return {};
    }

    static getGroupPageSearch() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.groupPage?.search || "";
        }
        return "";
    }

    static getGroupPageRowsPerPage() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.groupPage?.rowsPerPage ?? 12;
        }
        return 12;
    }

    static getLicenseSidePanelFileTabParams() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        const defaultParams: LicenseFileTabParams = {
            workspaceId: 0,
            groups: [],
            sortCriteria: "filename",
            sortDirection: "desc",
            sourceTypes: [SourceType.User],
            includeParentFiles: false,
        };

        if (workspaceId) {
            const saved = settings?.[workspaceId]?.licenseSidePanel?.fileTab?.params;
            if (!saved) {
                return { ...defaultParams };
            }
            return { ...defaultParams, ...saved };
        }
        return defaultParams;
    }

    static getLicenseSidePanelAppsTabSystemFilter() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.licenseSidePanel?.appsTab?.columnFilters || [];
        }
        return [];
    }

    static getProductPageCollapsedSections() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.productPage?.collapsedSections || {};
        }
        return {};
    }

    static getProvisioningPageCollapsedSections() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.provisioningPage?.collapsedSections || {};
        }
        return {};
    }

    static getProvisioningPageLastSelectedProvisioning() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.provisioningPage?.lastSelectedProvisioning || null;
        }
        return null;
    }

    static getOverviewPageDayRange() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.overviewPage?.dayRange || TimeRangeOptions[0].value;
        }
        return TimeRangeOptions[0].value;
    }

    static getOverviewPageGroupFilter() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.overviewPage?.groupFilter || [];
        }
        return [];
    }

    static getRolloutsPageLastSelectedHardware() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.rolloutsPage?.lastSelectedHardware || null;
        }
        return null;
    }

    static getProductPageLastSelectedProduct() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.productPage?.lastSelectedProduct || null;
        }
        return null;
    }

    static getWorkspacePageLastExpirationBannerDate() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.workspacePage?.lastExpirationBannerDate || null;
        }
        return null;
    }

    static getDownloadsPageColumnFilters(): ColumnFiltersState {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (!workspaceId) {
            return defaultDownloadsPageColumnFilters;
        }

        const storedFilters = settings?.[workspaceId]?.downloadsPage?.columnFilters;

        return storedFilters ?? defaultDownloadsPageColumnFilters;
    }
    static getDownloadsPageSorting() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.downloadsPage?.sorting || [{ id: "created", desc: true }];
        }
        return [{ id: "created", desc: true }];
    }
    static getDownloadsPageRowsPerPage() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.downloadsPage?.rowsPerPage ?? 12;
        }
        return 12;
    }

    static getClientsPageRowsPerPage() {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.clientsPage?.rowsPerPage ?? 12;
        }
        return 12;
    }

    static getClientsPageColumnFilters(): ColumnFiltersState {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return (settings?.[workspaceId]?.clientsPage?.columnFilters || []) as ColumnFiltersState;
        }
        return [];
    }

    static getLicenseSidePanelHardwareTabExpandedSections(): Record<string, boolean> | undefined {
        const workspaceId = LocalStorage.getItem<string | null>("currentWorkspace");
        const settings = this.getUserSettings();

        if (workspaceId) {
            return settings?.[workspaceId]?.licenseSidePanel?.hardwareTab?.expandedSections;
        }
        return undefined;
    }
}
export default UserSettingsService;
